import type { CSSProperties, ReactNode } from "react";

export type GlassTagKind = "achievement" | "audioQuality" | "neutral";

export interface GlassTagProps {
	readonly children: ReactNode;
	readonly kind?: GlassTagKind;
	/** Render on top of media (white text, translucent fill). */
	readonly onMedia?: boolean;
	readonly selected?: boolean;
	readonly className?: string;
}

function tagStyle(
	kind: GlassTagKind,
	onMedia: boolean,
	selected: boolean,
): CSSProperties {
	if (onMedia) {
		return {
			color: "var(--ds-on-media-primary)",
			background: "rgb(0 0 0 / 0.42)",
			borderColor:
				"color-mix(in srgb, var(--ds-on-media-primary) 35%, transparent)",
			backdropFilter: "blur(12px)",
			WebkitBackdropFilter: "blur(12px)",
		};
	}
	switch (kind) {
		case "achievement":
			return {
				color: "var(--ds-background)",
				background: "var(--ds-accent)",
				borderColor: "var(--ds-accent)",
			};
		case "audioQuality":
			return {
				color: "var(--ds-text-primary)",
				background:
					"color-mix(in srgb, var(--ds-accent) 18%, var(--ds-surface))",
				borderColor: "color-mix(in srgb, var(--ds-accent) 45%, transparent)",
			};
		default:
			return {
				color: selected ? "var(--ds-accent)" : "var(--ds-text-primary)",
				background: "var(--ds-glass-soft)",
				borderColor: selected ? "var(--ds-accent)" : "var(--ds-glass-stroke)",
			};
	}
}

/**
 * GlassTag — small pill tag for achievements, audio quality, or neutral labels.
 * Mirrors `DSGlassTag` on iOS with an on-media variant.
 */
export function GlassTag({
	children,
	kind = "neutral",
	onMedia = false,
	selected = false,
	className = "",
}: Readonly<GlassTagProps>) {
	return (
		<span
			className={`ds-tag ds-tag--${kind}${onMedia ? " ds-tag--on-media" : ""}${selected ? " ds-tag--selected" : ""} ${className}`.trim()}
			style={tagStyle(kind, onMedia, selected)}
		>
			{children}
		</span>
	);
}
